import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import { Mutation, MutationUpdateAccessGrantArgs, Query, UpdateAccessGrantInput } from 'graphql/generated/graphqlTypes';
import { UPDATE_ACCESS_GRANT } from 'graphql/v2/mutations';
import { GET_ALL_VISITORS_GROUPS, GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES } from 'graphql/v2/queries';
import { filterGroupsForUser } from 'hooks/shared/filterGroupsForUser';
import { useEffect } from 'react';
import { setSchedulesAction, updateAccessGrantAction } from 'state/actions/schedules';
import { authVar, schedulesVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES_TYPED: TypedDocumentNode<Pick<Query, 'getVisitorGroupsForTenant'>> =
  GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES;
const UPDATE_ACCESS_GRANT_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateAccessGrant'>,
  MutationUpdateAccessGrantArgs
> = UPDATE_ACCESS_GRANT;

export type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const useSchedules = ({ handleFetchError }: BaseHookProps) => {
  const { user } = useReactiveVar(authVar);
  const schedules = useReactiveVar(schedulesVar);

  const [getVisitorGroupsWithSchedules, { loading }] = useLazyQuery(
    GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES_TYPED,
    { fetchPolicy: 'network-only' }
  );

  const [updateAccessGrantMutation, { loading: updateAccessGrantMutationLoading }] = useMutation(
    UPDATE_ACCESS_GRANT_TYPED,
    {
      refetchQueries: [GET_ALL_VISITORS_GROUPS],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        updateAccessGrantAction(data.updateAccessGrant);
      },
      onError: () => {
        handleFetchError('Error while updating schedule');
      }
    }
  );

  useEffect(() => {
    const fetchVisitorGroupsWithSchedules = async () => {
      try {
        const { data } = await getVisitorGroupsWithSchedules();
        if (data) {
          const groups = filterGroupsForUser(data.getVisitorGroupsForTenant, user);
          setSchedulesAction(groups);
        }
      } catch {
        handleFetchError('Error while fetching schedules');
      }
    };
    void fetchVisitorGroupsWithSchedules();
  }, [getVisitorGroupsWithSchedules]);

  const updateAccessGrant = async (accessGrantId: string, input: UpdateAccessGrantInput) => {
    const { data } = await ignoreHandledMutationError(
      updateAccessGrantMutation({
        variables: {
          accessGrantId,
          input
        },
        // Here Apollo doesn't update cache automatically, we have to do it manually
        update(cache, { data: updatedSchedule }) {
          const updatedVisitorGroups = schedules.map((group) => ({
            ...group,
            accessGrants: group.accessGrants.map((grant) =>
              grant.id === updatedSchedule?.updateAccessGrant.id ? updatedSchedule.updateAccessGrant : grant
            )
          }));

          cache.writeQuery({
            query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES,
            data: {
              getVisitorGroupsForTenant: updatedVisitorGroups
            }
          });
        }
      })
    );
    return data;
  };

  return {
    schedules,
    updateAccessGrant,
    updateAccessGrantMutationLoading,
    loading
  };
};

export default useSchedules;
